import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FileText,
  Search,
  Plus,
  Printer,
  FileSpreadsheet,
  Check,
  Clock,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Building2,
  X,
  ClipboardList,
  PlusCircle,
  MinusCircle,
  Package,
  Users,
  Edit3,
  ChevronsUpDown,
  BarChart3
} from 'lucide-react';

// Searchable Combobox Component
const SearchableSelect = ({ options, value, onChange, placeholder, icon: Icon, disabled }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const selectedOption = options.find(opt => opt.id === value);
  
  const filteredOptions = options.filter(opt => 
    opt.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opt.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className="flex items-center justify-between w-full px-3 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2 truncate">
            {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
            <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
              {selectedOption?.name || selectedOption?.label || placeholder}
            </span>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={`ابحث...`} 
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="text-right"
          />
          <CommandList>
            <CommandEmpty>لا توجد نتائج</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onChange(option.id);
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span>{option.name || option.label}</span>
                  {value === option.id && (
                    <Check className="w-4 h-4 text-blue-600" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Reports Modal — search invoices across delivery sheets
const InvoiceReportsModal = ({ onClose, suppliers }) => {
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [openingDelivery, setOpeningDelivery] = useState(null);
  const [searched, setSearched] = useState(false);
  const [viewDelivery, setViewDelivery] = useState(null);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    const q = (invoiceQuery || '').trim();
    const s = (supplierFilter || '').trim();
    if (!q && !s) {
      toast.error('يجب إدخال رقم فاتورة أو اختيار مورد للبحث');
      return;
    }
    try {
      setSearching(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (q) params.append('invoice_number', q);
      if (s) params.append('supplier_name', s);
      if (statusFilter) params.append('status', statusFilter);
      const res = await fetch(`${API}/invoices/reports/search-invoices?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('فشل البحث');
      const data = await res.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
      setSearched(true);
    } catch (err) {
      toast.error('فشل في البحث عن الفواتير');
    } finally {
      setSearching(false);
    }
  };

  const openDelivery = async (delivery_id) => {
    try {
      setOpeningDelivery(delivery_id);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/invoices/delivery/${delivery_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('فشل جلب الكشف');
      const delivery = await res.json();
      setViewDelivery(delivery);
    } catch (err) {
      toast.error('تعذّر فتح تفاصيل الكشف');
    } finally {
      setOpeningDelivery(null);
    }
  };

  const resetAll = () => {
    setInvoiceQuery('');
    setSupplierFilter('');
    setStatusFilter('');
    setResults([]);
    setTotal(0);
    setSearched(false);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('ar-SA'); } catch { return d; }
  };
  const formatMoney = (a) => {
    if (a == null || a === '') return '-';
    try { return new Intl.NumberFormat('ar-SA', { minimumFractionDigits: 2 }).format(a); } catch { return String(a); }
  };
  const statusBadge = (s) => {
    if (s === 'received') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">✓ تم الاستلام</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full">⏳ قيد الانتظار</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="relative bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="invoice-reports-modal"
      >
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-l from-indigo-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">تقرير البحث عن الفواتير</h2>
              <p className="text-xs text-gray-500">ابحث برقم الفاتورة داخل كشوف التسليم — اضغط الرقم لعرض تفاصيل الكشف</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-reports-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="p-4 border-b bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <label className="block text-xs text-gray-600 mb-1 font-semibold">رقم الفاتورة</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={invoiceQuery}
                  onChange={(e) => setInvoiceQuery(e.target.value)}
                  placeholder="مثال: 12345 أو جزء من الرقم..."
                  data-testid="report-invoice-input"
                  className="w-full pr-9 pl-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs text-gray-600 mb-1 font-semibold">فلترة بالمورد</label>
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                data-testid="report-supplier-select"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
              >
                <option value="">كل الموردين</option>
                {(suppliers || []).map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-600 mb-1 font-semibold">حالة الكشف</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="report-status-select"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
              >
                <option value="">كل الحالات</option>
                <option value="pending">قيد الانتظار</option>
                <option value="received">تم الاستلام</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="submit"
              disabled={searching}
              data-testid="report-search-btn"
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition"
            >
              <Search className="w-4 h-4" />
              {searching ? 'جارٍ البحث...' : 'بحث'}
            </button>
            <button
              type="button"
              onClick={resetAll}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              إعادة تعيين
            </button>
            {searched && (
              <div className="mr-auto text-xs text-gray-600">
                عدد النتائج: <span className="font-bold text-indigo-700">{total}</span>
              </div>
            )}
          </div>
        </form>

        {/* Results */}
        <div className="flex-1 overflow-auto p-4">
          {!searched ? (
            <div className="text-center py-16 text-gray-400">
              <Search className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p className="text-sm">أدخل رقم فاتورة أو اختر مورد ثم اضغط {'"بحث"'}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p className="text-sm">لا توجد نتائج مطابقة</p>
            </div>
          ) : (
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '6%' }} />
              </colgroup>
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">#</th>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">رقم الفاتورة</th>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">المورد</th>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">المشروع</th>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">تاريخ الفاتورة</th>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">المبلغ</th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">رقم الكشف</th>
                  <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">تاريخ التسليم</th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">الحالة</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={`${r.delivery_id}-${r.invoice_number}-${i}`} className="border-t hover:bg-indigo-50/40 align-middle">
                    <td className="px-2 py-2 text-gray-500 text-xs">{i + 1}</td>
                    <td className="px-2 py-2 font-semibold text-xs">
                      <button
                        type="button"
                        onClick={() => openDelivery(r.delivery_id)}
                        disabled={openingDelivery === r.delivery_id}
                        data-testid={`open-delivery-invoice-${i}`}
                        title="اضغط لفتح تفاصيل كشف التسليم"
                        className="text-indigo-700 hover:text-indigo-900 hover:underline truncate w-full text-right disabled:opacity-60"
                      >
                        {r.invoice_number}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <div className="truncate" title={r.supplier_name}>{r.supplier_name || '-'}</div>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <div className="truncate" title={`${r.project_number || ''} ${r.project_name || ''}`}>
                        {r.project_number ? <span className="text-gray-500">{r.project_number} - </span> : null}
                        {r.project_name || (r.project_number ? '' : '-')}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-700">{r.invoice_date ? formatDate(r.invoice_date) : '-'}</td>
                    <td className="px-2 py-2 text-xs font-mono text-gray-800">{formatMoney(r.amount)}</td>
                    <td className="px-2 py-2 text-center text-xs">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-bold text-xs">
                        #{r.delivery_number}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-700">{formatDate(r.delivery_date)}</td>
                    <td className="px-2 py-2 text-center">{statusBadge(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Nested delivery details view (stays inside reports modal) */}
        {viewDelivery && (
          <NestedDeliveryDetails
            delivery={viewDelivery}
            onBack={() => setViewDelivery(null)}
          />
        )}
      </div>
    </div>
  );
};

// Delivery details view rendered on top of the reports modal without closing it
const NestedDeliveryDetails = ({ delivery, onBack }) => {
  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('ar-SA'); } catch { return d; }
  };
  const formatMoney = (a) => {
    if (a == null || a === '') return '-';
    try { return new Intl.NumberFormat('ar-SA', { minimumFractionDigits: 2 }).format(a); } catch { return String(a); }
  };
  const getSupplierTotal = (invoices) => (invoices || []).reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
  const getDeliveryTotal = () => {
    if (delivery.suppliers && delivery.suppliers.length > 0) {
      return delivery.suppliers.reduce((sum, s) => sum + getSupplierTotal(s.invoices), 0);
    }
    return getSupplierTotal(delivery.invoices);
  };

  // Normalize to always render as suppliers[]
  const suppliers = (delivery.suppliers && delivery.suppliers.length > 0)
    ? delivery.suppliers
    : [{ supplier_name: delivery.supplier_name, invoices: delivery.invoices || [] }];

  const handlePrint = () => {
    const printContent = document.getElementById('nested-print-content');
    if (!printContent) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><title>كشف تسليم فواتير #${delivery.delivery_number}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Segoe UI',Tahoma,Arial,sans-serif;padding:20px;direction:rtl;}
        .receipt{max-width:900px;margin:0 auto;border:2px solid #333;padding:25px;}
        .header{text-align:center;border-bottom:2px solid #333;padding-bottom:15px;margin-bottom:20px;}
        .header h1{font-size:22px;margin-bottom:5px;}
        .header h2{font-size:16px;color:#666;}
        .delivery-info{display:flex;justify-content:space-between;background:#f0f0f0;padding:10px 15px;border-radius:5px;margin-bottom:20px;}
        .delivery-info div{text-align:center;}
        .delivery-info .label{font-size:11px;color:#666;}
        .delivery-info .value{font-weight:bold;font-size:14px;}
        .supplier-section{margin-bottom:20px;border:1px solid #ddd;border-radius:8px;overflow:hidden;}
        .supplier-header{background:#e8f4fc;padding:10px 15px;font-weight:bold;border-bottom:1px solid #ddd;display:flex;justify-content:space-between;}
        .invoices-table{width:100%;border-collapse:collapse;}
        .invoices-table th,.invoices-table td{border:1px solid #ddd;padding:8px;text-align:right;font-size:12px;}
        .invoices-table th{background:#f5f5f5;font-weight:bold;}
        .invoices-table tr:nth-child(even){background:#fafafa;}
        .grand-total{background:#d4edda;padding:12px;border-radius:5px;text-align:center;font-size:16px;font-weight:bold;margin-top:15px;}
        @media print{body{padding:0;}.receipt{border:1px solid #000;}}
      </style></head><body>${printContent.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 250);
  };

  return (
    <div className="absolute inset-0 bg-white z-10 flex flex-col rounded-2xl overflow-hidden" data-testid="nested-delivery-details">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-l from-blue-50 to-white">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            data-testid="back-to-reports-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
            title="الرجوع إلى نتائج البحث"
          >
            <ChevronRight className="w-4 h-4" />
            رجوع
          </button>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-bold text-gray-800">تفاصيل كشف التسليم</h3>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-sm font-bold">#{delivery.delivery_number}</span>
          </div>
        </div>
        <button
          onClick={handlePrint}
          data-testid="print-delivery-btn"
          className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
        >
          <Printer className="w-4 h-4" />
          طباعة
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-5 bg-gray-50">
        {/* Info cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <InfoCard label="رقم الكشف" value={`#${delivery.delivery_number}`} accent="blue" />
          <InfoCard label="تاريخ التسليم" value={formatDate(delivery.delivery_date)} accent="indigo" />
          <InfoCard label="الحالة" value={delivery.status === 'received' ? '✓ تم الاستلام' : '⏳ قيد الانتظار'} accent={delivery.status === 'received' ? 'green' : 'yellow'} />
          <InfoCard label="عدد الموردين" value={delivery.suppliers_count || suppliers.length} accent="purple" />
          <InfoCard label="عدد الفواتير" value={delivery.invoices_count || suppliers.reduce((s, x) => s + (x.invoices?.length || 0), 0)} accent="teal" />
        </div>

        {/* Delivered/Received info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="bg-white border rounded-xl p-3 flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500">المُسلِّم (المشتريات)</div>
              <div className="font-semibold text-gray-800 text-sm">{delivery.created_by || '-'}</div>
            </div>
          </div>
          <div className="bg-white border rounded-xl p-3 flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Check className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500">المُستلِم (المحاسبة)</div>
              <div className="font-semibold text-gray-800 text-sm">
                {delivery.received_by ? `${delivery.received_by} — ${delivery.received_date || ''}` : 'لم يتم الاستلام بعد'}
              </div>
            </div>
          </div>
        </div>

        {/* Suppliers + invoices */}
        {suppliers.map((sup, idx) => (
          <div key={idx} className="bg-white border rounded-xl overflow-hidden mb-4 shadow-sm">
            <div className="flex items-center justify-between bg-gradient-to-l from-blue-50 to-white px-4 py-2.5 border-b">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="font-bold text-gray-800 text-sm">{idx + 1}. {sup.supplier_name || '-'}</span>
                <span className="text-xs px-2 py-0.5 bg-white border rounded-md text-gray-600">
                  {sup.invoices?.length || 0} فواتير
                </span>
              </div>
              {getSupplierTotal(sup.invoices) > 0 && (
                <span className="text-xs font-bold text-gray-700">
                  الإجمالي: <span className="text-blue-700 font-mono">{formatMoney(getSupplierTotal(sup.invoices))}</span>
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 border-b" style={{width: '40px'}}>#</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 border-b">رقم الفاتورة</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 border-b">المشروع</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 border-b">تاريخ الفاتورة</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 border-b">المبلغ</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 border-b">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {(sup.invoices || []).map((inv, i) => (
                    <tr key={i} className="border-b last:border-b-0 hover:bg-blue-50/30">
                      <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                      <td className="px-3 py-2 font-semibold text-gray-800">{inv.invoice_number || '-'}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {inv.project_number ? <span className="text-gray-500 text-[10px]">{inv.project_number} - </span> : null}
                        {inv.project_name || (inv.project_number ? '' : (delivery.project_name || '-'))}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{inv.invoice_date ? formatDate(inv.invoice_date) : '-'}</td>
                      <td className="px-3 py-2 font-mono text-gray-800">{formatMoney(inv.amount)}</td>
                      <td className="px-3 py-2 text-gray-600">{inv.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Grand total */}
        {getDeliveryTotal() > 0 && (
          <div className="bg-gradient-to-l from-emerald-50 to-white border border-emerald-200 rounded-xl px-5 py-3 flex items-center justify-between mb-4">
            <span className="font-bold text-gray-700 text-sm">الإجمالي الكلي للكشف</span>
            <span className="font-mono font-bold text-emerald-700 text-lg">{formatMoney(getDeliveryTotal())}</span>
          </div>
        )}

        {/* Notes */}
        {delivery.notes && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
            <div className="text-xs font-bold text-yellow-800 mb-1">ملاحظات عامة</div>
            <div className="text-sm text-gray-700">{delivery.notes}</div>
          </div>
        )}

        {/* Hidden content for printing (reuses print CSS class names) */}
        <div id="nested-print-content" style={{ display: 'none' }}>
          <div className="receipt">
            <div className="header">
              <h1>كشف تسليم فواتير للحسابات</h1>
              <h2>قسم المشتريات</h2>
            </div>
            <div className="delivery-info">
              <div><div className="label">رقم الكشف</div><div className="value">{delivery.delivery_number}</div></div>
              <div><div className="label">تاريخ التسليم</div><div className="value">{formatDate(delivery.delivery_date)}</div></div>
              <div><div className="label">المسلم</div><div className="value">{delivery.created_by || '-'}</div></div>
              <div><div className="label">عدد الموردين</div><div className="value">{suppliers.length}</div></div>
              <div><div className="label">عدد الفواتير</div><div className="value">{delivery.invoices_count || suppliers.reduce((s, x) => s + (x.invoices?.length || 0), 0)}</div></div>
            </div>
            {suppliers.map((sup, idx) => (
              <div key={idx} className="supplier-section">
                <div className="supplier-header">
                  <span>{idx + 1}. {sup.supplier_name}</span>
                  <span>({sup.invoices?.length || 0} فواتير)</span>
                </div>
                <table className="invoices-table">
                  <thead>
                    <tr>
                      <th style={{width: '30px'}}>#</th>
                      <th>رقم الفاتورة</th>
                      <th>المشروع</th>
                      <th>تاريخ الفاتورة</th>
                      <th>المبلغ</th>
                      <th>ملاحظات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sup.invoices || []).map((inv, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{inv.invoice_number}</td>
                        <td>{inv.project_name || inv.project_number || '-'}</td>
                        <td>{inv.invoice_date ? formatDate(inv.invoice_date) : '-'}</td>
                        <td>{inv.amount ? formatMoney(inv.amount) : '-'}</td>
                        <td>{inv.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            {getDeliveryTotal() > 0 && (
              <div className="grand-total">الإجمالي الكلي: {formatMoney(getDeliveryTotal())}</div>
            )}
            {delivery.notes && (
              <div style={{marginTop: '15px', padding: '10px', background: '#f9f9f9', borderRadius: '5px'}}>
                <strong>ملاحظات عامة:</strong> {delivery.notes}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ label, value, accent = 'blue' }) => {
  const accents = {
    blue: 'from-blue-50 border-blue-200 text-blue-700',
    indigo: 'from-indigo-50 border-indigo-200 text-indigo-700',
    green: 'from-green-50 border-green-200 text-green-700',
    yellow: 'from-yellow-50 border-yellow-200 text-yellow-700',
    purple: 'from-purple-50 border-purple-200 text-purple-700',
    teal: 'from-teal-50 border-teal-200 text-teal-700',
  };
  return (
    <div className={`bg-gradient-to-b ${accents[accent]} to-white border rounded-xl px-3 py-2.5 shadow-sm`}>
      <div className="text-[10px] text-gray-500 font-semibold mb-1">{label}</div>
      <div className="text-base font-bold">{value}</div>
    </div>
  );
};


const InvoiceDelivery = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showReports, setShowReports] = useState(false);

  const emptyInvoice = { invoice_number: '', invoice_date: '', amount: '', project_id: '', project_number: '', project_name: '', notes: '' };
  const emptySupplier = { supplier_id: '', supplier_name: '', invoices: [{ ...emptyInvoice }] };

  const [formData, setFormData] = useState({
    suppliers: [{ ...emptySupplier }],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchDeliveries();
  }, [page, statusFilter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const suppliersRes = await fetch(`${API}/procurement/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (suppliersRes.ok) {
        const suppliersData = await suppliersRes.json();
        setSuppliers(suppliersData || []);
      }
      
      const projectsRes = await fetch(`${API}/procurement/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        // Format projects for searchable select
        const formattedProjects = (projectsData || []).map(p => ({
          ...p,
          label: `${p.project_number} - ${p.project_name}`
        }));
        setProjects(formattedProjects);
      }
      
      const statsRes = await fetch(`${API}/invoices/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/invoices/deliveries?page=${page}&limit=15`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('فشل في جلب البيانات');
      
      const data = await response.json();
      setDeliveries(data.deliveries || []);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchDeliveries();
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/invoices/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('فشل في البحث');
      
      const data = await response.json();
      setDeliveries(data.deliveries || []);
      setTotalPages(1);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // إدارة الموردين - إضافة في الأعلى
  const addSupplier = () => {
    setFormData({
      ...formData,
      suppliers: [{ ...emptySupplier, invoices: [{ ...emptyInvoice }] }, ...formData.suppliers]
    });
  };

  const removeSupplier = (supplierIndex) => {
    if (formData.suppliers.length > 1) {
      const newSuppliers = formData.suppliers.filter((_, i) => i !== supplierIndex);
      setFormData({ ...formData, suppliers: newSuppliers });
    }
  };

  const handleSupplierChange = (supplierIndex, supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    const newSuppliers = [...formData.suppliers];
    newSuppliers[supplierIndex] = {
      ...newSuppliers[supplierIndex],
      supplier_id: supplierId,
      supplier_name: supplier?.name || ''
    };
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  // إدارة الفواتير
  const addInvoice = (supplierIndex) => {
    const newSuppliers = [...formData.suppliers];
    newSuppliers[supplierIndex].invoices = [{ ...emptyInvoice }, ...newSuppliers[supplierIndex].invoices];
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  const removeInvoice = (supplierIndex, invoiceIndex) => {
    const newSuppliers = [...formData.suppliers];
    if (newSuppliers[supplierIndex].invoices.length > 1) {
      newSuppliers[supplierIndex].invoices = newSuppliers[supplierIndex].invoices.filter((_, i) => i !== invoiceIndex);
      setFormData({ ...formData, suppliers: newSuppliers });
    }
  };

  const updateInvoice = (supplierIndex, invoiceIndex, field, value) => {
    const newSuppliers = [...formData.suppliers];
    newSuppliers[supplierIndex].invoices[invoiceIndex] = {
      ...newSuppliers[supplierIndex].invoices[invoiceIndex],
      [field]: value
    };
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  const handleProjectChange = (supplierIndex, invoiceIndex, projectId) => {
    const project = projects.find(p => p.id === projectId);
    const newSuppliers = [...formData.suppliers];
    newSuppliers[supplierIndex].invoices[invoiceIndex] = {
      ...newSuppliers[supplierIndex].invoices[invoiceIndex],
      project_id: projectId,
      project_number: project?.project_number || '',
      project_name: project?.project_name || ''
    };
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validSuppliers = formData.suppliers.filter(s => {
      if (!s.supplier_name) return false;
      const validInvoices = s.invoices.filter(inv => inv.invoice_number.trim());
      return validInvoices.length > 0;
    });
    
    if (validSuppliers.length === 0) {
      toast.error('يرجى إضافة مورد واحد على الأقل مع فاتورة');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const submitData = {
        ...formData,
        suppliers: validSuppliers.map(s => ({
          supplier_id: s.supplier_id,
          supplier_name: s.supplier_name,
          invoices: s.invoices.filter(inv => inv.invoice_number.trim()).map(inv => ({
            ...inv,
            amount: inv.amount ? parseFloat(inv.amount) : null
          }))
        }))
      };
      
      let response;
      if (editMode && editingDelivery) {
        response = await fetch(`${API}/invoices/delivery/${editingDelivery.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submitData)
        });
      } else {
        response = await fetch(`${API}/invoices/delivery`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submitData)
        });
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'فشل في حفظ التسليم');
      }
      
      const savedDelivery = await response.json();
      
      if (editMode) {
        toast.success(`تم تحديث الكشف رقم ${savedDelivery.delivery_number}`);
      } else {
        toast.success(`تم إنشاء الكشف رقم ${savedDelivery.delivery_number} (${savedDelivery.suppliers_count} موردين - ${savedDelivery.invoices_count} فواتير)`);
      }
      
      resetForm();
      setShowForm(false);
      setEditMode(false);
      setEditingDelivery(null);
      
      if (!editMode) {
        setSelectedDelivery(savedDelivery);
        setShowPrintModal(true);
      }
      
      fetchDeliveries();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      suppliers: [{ ...emptySupplier, invoices: [{ ...emptyInvoice }] }],
      notes: ''
    });
    setEditMode(false);
    setEditingDelivery(null);
  };

  const handleEdit = (delivery) => {
    const suppliersData = delivery.suppliers?.map(s => ({
      supplier_id: s.supplier_id || '',
      supplier_name: s.supplier_name || '',
      invoices: s.invoices?.map(inv => ({
        invoice_number: inv.invoice_number || '',
        invoice_date: inv.invoice_date || '',
        amount: inv.amount || '',
        project_id: inv.project_id || '',
        project_number: inv.project_number || '',
        project_name: inv.project_name || '',
        notes: inv.notes || ''
      })) || [{ ...emptyInvoice }]
    })) || [{ ...emptySupplier }];
    
    setFormData({
      suppliers: suppliersData,
      notes: delivery.notes || ''
    });
    setEditMode(true);
    setEditingDelivery(delivery);
    setShowForm(true);
  };

  const handleReceive = async (delivery) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/invoices/delivery/${delivery.id}/receive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('فشل في تأكيد الاستلام');
      
      toast.success('تم تأكيد الاستلام');
      fetchDeliveries();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (delivery) => {
    if (!window.confirm(`هل أنت متأكد من حذف الكشف رقم ${delivery.delivery_number}؟`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/invoices/delivery/${delivery.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('فشل في الحذف');
      
      toast.success('تم حذف الكشف');
      fetchDeliveries();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <title>كشف تسليم فواتير</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: rtl; }
          .receipt { max-width: 900px; margin: 0 auto; border: 2px solid #333; padding: 25px; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 22px; margin-bottom: 5px; }
          .header h2 { font-size: 16px; color: #666; }
          .delivery-info { display: flex; justify-content: space-between; background: #f0f0f0; padding: 10px 15px; border-radius: 5px; margin-bottom: 20px; }
          .delivery-info div { text-align: center; }
          .delivery-info .label { font-size: 11px; color: #666; }
          .delivery-info .value { font-weight: bold; font-size: 14px; }
          .supplier-section { margin-bottom: 20px; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
          .supplier-header { background: #e8f4fc; padding: 10px 15px; font-weight: bold; border-bottom: 1px solid #ddd; display: flex; justify-content: space-between; }
          .invoices-table { width: 100%; border-collapse: collapse; }
          .invoices-table th, .invoices-table td { border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 12px; }
          .invoices-table th { background: #f5f5f5; font-weight: bold; }
          .invoices-table tr:nth-child(even) { background: #fafafa; }
          .supplier-total { background: #e8f4fc; font-weight: bold; }
          .signature-section { margin-top: 40px; display: flex; justify-content: space-between; }
          .signature-box { text-align: center; width: 45%; }
          .signature-line { border-bottom: 1px solid #333; height: 60px; margin-bottom: 5px; }
          .signature-label { font-size: 14px; color: #666; }
          .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #888; border-top: 1px dashed #ccc; padding-top: 10px; }
          .grand-total { background: #d4edda; padding: 12px; border-radius: 5px; text-align: center; font-size: 16px; font-weight: bold; margin-top: 15px; }
          @media print { body { padding: 0; } .receipt { border: 1px solid #000; } }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA');
  };

  const formatMoney = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('ar-SA', { minimumFractionDigits: 2 }).format(amount);
  };

  const getSupplierTotal = (invoices) => {
    return invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  };

  const getDeliveryTotal = (suppliers) => {
    return suppliers?.reduce((sum, s) => sum + getSupplierTotal(s.invoices || []), 0) || 0;
  };

  const getSuppliersNames = (delivery) => {
    if (delivery.suppliers && delivery.suppliers.length > 0) {
      return delivery.suppliers.map(s => s.supplier_name).join('، ');
    }
    return delivery.supplier_name || '-';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardList className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">كشف تسليم فواتير الموردين</h1>
              <p className="text-sm text-gray-500">نظام تسليم الفواتير للحسابات</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  setExporting(true);
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API}/invoices/deliveries/export-all.xlsx`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  if (!res.ok) {
                    toast.error('فشل في تصدير الملف');
                    return;
                  }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `كل-كشوف-تسليم-الفواتير-${new Date().toISOString().slice(0, 10)}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success('تم تصدير الملف بنجاح');
                } catch (e) {
                  toast.error('فشل في التصدير');
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              data-testid="export-all-invoice-deliveries-btn"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition-colors"
              title="تصدير كل الكشوف وفواتيرها كملف Excel"
            >
              <FileSpreadsheet className="w-5 h-5" />
              {exporting ? 'جارٍ التصدير...' : 'تصدير شامل لأكسل'}
            </button>
            <button
              onClick={() => setShowReports(true)}
              data-testid="open-reports-btn"
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              title="تقارير البحث عن الفواتير في كشوف التسليم"
            >
              <BarChart3 className="w-5 h-5" />
              تقارير
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              كشف جديد
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-gray-500 text-sm mb-1">إجمالي الكشوفات</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total_deliveries || 0}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-gray-500 text-sm mb-1">قيد الانتظار</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-gray-500 text-sm mb-1">تم الاستلام</div>
            <div className="text-2xl font-bold text-green-600">{stats.received || 0}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-gray-500 text-sm mb-1">إجمالي الموردين</div>
            <div className="text-2xl font-bold text-purple-600">{stats.total_suppliers || 0}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-gray-500 text-sm mb-1">إجمالي الفواتير</div>
            <div className="text-2xl font-bold text-blue-600">{stats.total_invoices || 0}</div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="بحث بالمورد، رقم المشروع، رقم الفاتورة، أو رقم الكشف..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <button onClick={handleSearch} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              بحث
            </button>
            
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">جميع الحالات</option>
              <option value="pending">قيد الانتظار</option>
              <option value="received">تم الاستلام</option>
            </select>
            
            <button
              onClick={() => { setSearchQuery(''); setStatusFilter(''); setPage(1); fetchDeliveries(); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Deliveries Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
          ) : deliveries.length === 0 ? (
            <div className="p-8 text-center text-gray-500">لا توجد كشوفات</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">رقم الكشف</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">الموردين</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">عدد الموردين</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">عدد الفواتير</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">تاريخ التسليم</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">الحالة</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deliveries.map((delivery) => (
                    <tr key={delivery.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-bold text-blue-600">#{delivery.delivery_number}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 max-w-[200px] truncate" title={getSuppliersNames(delivery)}>
                        {getSuppliersNames(delivery)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                          {delivery.suppliers_count || delivery.suppliers?.length || 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {delivery.invoices_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(delivery.delivery_date)}</td>
                      <td className="px-4 py-3 text-center">
                        {delivery.status === 'received' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <Check className="w-3 h-3" />
                            تم الاستلام
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            <Clock className="w-3 h-3" />
                            قيد الانتظار
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => { setSelectedDelivery(delivery); setShowPrintModal(true); }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="طباعة"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          {delivery.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleEdit(delivery)}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                                title="تعديل"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReceive(delivery)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                title="تأكيد الاستلام"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(delivery)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="حذف"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-gray-600">صفحة {page} من {totalPages}</div>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border disabled:opacity-50 hover:bg-gray-50">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border disabled:opacity-50 hover:bg-gray-50">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* New/Edit Delivery Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
                <h2 className="text-lg font-bold">
                  {editMode ? `تعديل الكشف رقم ${editingDelivery?.delivery_number}` : 'كشف تسليم فواتير جديد'}
                </h2>
                <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* الموردين */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      <Users className="w-4 h-4 inline-block ml-1" />
                      الموردين والفواتير *
                    </label>
                    <button
                      type="button"
                      onClick={addSupplier}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <PlusCircle className="w-4 h-4" />
                      إضافة مورد
                    </button>
                  </div>

                  <div className="space-y-4">
                    {formData.suppliers.map((supplier, sIdx) => (
                      <div key={sIdx} className="border rounded-xl overflow-hidden bg-gray-50">
                        {/* رأس المورد */}
                        <div className="flex items-center gap-3 p-3 bg-blue-50 border-b">
                          <div className="flex-1">
                            <SearchableSelect
                              options={suppliers}
                              value={supplier.supplier_id}
                              onChange={(id) => handleSupplierChange(sIdx, id)}
                              placeholder="ابحث واختر المورد *"
                              icon={Building2}
                            />
                          </div>
                          {formData.suppliers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSupplier(sIdx)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              title="حذف المورد"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* فواتير المورد */}
                        <div className="p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">فواتير المورد</span>
                            <button
                              type="button"
                              onClick={() => addInvoice(sIdx)}
                              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700"
                            >
                              <PlusCircle className="w-3 h-3" />
                              إضافة فاتورة
                            </button>
                          </div>

                          {supplier.invoices.map((inv, iIdx) => (
                            <div key={iIdx} className="bg-white p-3 rounded-lg border space-y-2">
                              <div className="flex gap-2 items-center">
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    placeholder="رقم الفاتورة *"
                                    value={inv.invoice_number}
                                    onChange={(e) => updateInvoice(sIdx, iIdx, 'invoice_number', e.target.value)}
                                    className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    required
                                  />
                                </div>
                                <div className="w-32">
                                  <input
                                    type="date"
                                    value={inv.invoice_date}
                                    onChange={(e) => updateInvoice(sIdx, iIdx, 'invoice_date', e.target.value)}
                                    className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="w-24">
                                  <input
                                    type="number"
                                    placeholder="المبلغ"
                                    value={inv.amount}
                                    onChange={(e) => updateInvoice(sIdx, iIdx, 'amount', e.target.value)}
                                    className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    step="0.01"
                                  />
                                </div>
                                {supplier.invoices.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeInvoice(sIdx, iIdx)}
                                    className="p-1 text-red-400 hover:text-red-600"
                                  >
                                    <MinusCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              {/* المشروع للفاتورة - قابل للبحث */}
                              <div className="flex gap-2 items-center">
                                <div className="flex-1">
                                  <SearchableSelect
                                    options={projects}
                                    value={inv.project_id}
                                    onChange={(id) => handleProjectChange(sIdx, iIdx, id)}
                                    placeholder="ابحث واختر المشروع (اختياري)"
                                    icon={Package}
                                  />
                                </div>
                                <input
                                  type="text"
                                  placeholder="ملاحظات"
                                  value={inv.notes}
                                  onChange={(e) => updateInvoice(sIdx, iIdx, 'notes', e.target.value)}
                                  className="w-32 px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* ملاحظات عامة */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ملاحظات عامة</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="ملاحظات إضافية (اختياري)"
                    rows={2}
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {editMode ? 'حفظ التعديلات' : 'حفظ وطباعة'}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Print Modal */}
        {showPrintModal && selectedDelivery && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
                <h2 className="text-lg font-bold">كشف تسليم فواتير</h2>
                <div className="flex gap-2">
                  <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <Printer className="w-4 h-4" />
                    طباعة
                  </button>
                  <button onClick={() => setShowPrintModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6" id="print-content">
                <div className="receipt">
                  <div className="header">
                    <h1>كشف تسليم فواتير للحسابات</h1>
                    <h2>قسم المشتريات</h2>
                  </div>
                  
                  <div className="delivery-info">
                    <div>
                      <div className="label">رقم الكشف</div>
                      <div className="value">{selectedDelivery.delivery_number}</div>
                    </div>
                    <div>
                      <div className="label">تاريخ التسليم</div>
                      <div className="value">{formatDate(selectedDelivery.delivery_date)}</div>
                    </div>
                    <div>
                      <div className="label">المسلم</div>
                      <div className="value">{selectedDelivery.created_by}</div>
                    </div>
                    <div>
                      <div className="label">عدد الموردين</div>
                      <div className="value">{selectedDelivery.suppliers_count || selectedDelivery.suppliers?.length || 1}</div>
                    </div>
                    <div>
                      <div className="label">عدد الفواتير</div>
                      <div className="value">{selectedDelivery.invoices_count || 0}</div>
                    </div>
                  </div>

                  {/* عرض الموردين وفواتيرهم */}
                  {selectedDelivery.suppliers && selectedDelivery.suppliers.length > 0 ? (
                    selectedDelivery.suppliers.map((supplier, idx) => (
                      <div key={idx} className="supplier-section">
                        <div className="supplier-header">
                          <span>{idx + 1}. {supplier.supplier_name}</span>
                          <span>({supplier.invoices?.length || 0} فواتير)</span>
                        </div>
                        <table className="invoices-table">
                          <thead>
                            <tr>
                              <th style={{width: '30px'}}>#</th>
                              <th>رقم الفاتورة</th>
                              <th>المشروع</th>
                              <th>تاريخ الفاتورة</th>
                              <th>المبلغ</th>
                              <th>ملاحظات</th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplier.invoices?.map((inv, iIdx) => (
                              <tr key={iIdx}>
                                <td>{iIdx + 1}</td>
                                <td>{inv.invoice_number}</td>
                                <td>{inv.project_name || inv.project_number || '-'}</td>
                                <td>{inv.invoice_date ? formatDate(inv.invoice_date) : '-'}</td>
                                <td>{inv.amount ? formatMoney(inv.amount) : '-'}</td>
                                <td>{inv.notes || '-'}</td>
                              </tr>
                            ))}
                            {supplier.invoices?.some(i => i.amount) && (
                              <tr className="supplier-total">
                                <td colSpan="4">إجمالي {supplier.supplier_name}</td>
                                <td>{formatMoney(getSupplierTotal(supplier.invoices))}</td>
                                <td></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ))
                  ) : (
                    <div className="supplier-section">
                      <div className="supplier-header">
                        <span>1. {selectedDelivery.supplier_name}</span>
                      </div>
                      <table className="invoices-table">
                        <thead>
                          <tr>
                            <th style={{width: '30px'}}>#</th>
                            <th>رقم الفاتورة</th>
                            <th>المشروع</th>
                            <th>تاريخ الفاتورة</th>
                            <th>المبلغ</th>
                            <th>ملاحظات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDelivery.invoices?.map((inv, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>{inv.invoice_number}</td>
                              <td>{inv.project_name || inv.project_number || '-'}</td>
                              <td>{inv.invoice_date ? formatDate(inv.invoice_date) : '-'}</td>
                              <td>{inv.amount ? formatMoney(inv.amount) : '-'}</td>
                              <td>{inv.notes || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {selectedDelivery.suppliers && getDeliveryTotal(selectedDelivery.suppliers) > 0 && (
                    <div className="grand-total">
                      الإجمالي الكلي: {formatMoney(getDeliveryTotal(selectedDelivery.suppliers))}
                    </div>
                  )}
                  
                  {selectedDelivery.notes && (
                    <div style={{marginTop: '15px', padding: '10px', background: '#f9f9f9', borderRadius: '5px'}}>
                      <strong>ملاحظات عامة:</strong> {selectedDelivery.notes}
                    </div>
                  )}
                  
                  <div className="signature-section">
                    <div className="signature-box">
                      <div className="signature-line"></div>
                      <div className="signature-label">توقيع المسلم (المشتريات)</div>
                    </div>
                    <div className="signature-box">
                      <div className="signature-line"></div>
                      <div className="signature-label">توقيع المستلم (المحاسبة)</div>
                    </div>
                  </div>
                  
                  <div className="footer">
                    تم إنشاء هذا الكشف آلياً - {new Date().toLocaleDateString('ar-SA')} {new Date().toLocaleTimeString('ar-SA')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Reports Modal */}
        {showReports && (
          <InvoiceReportsModal
            onClose={() => setShowReports(false)}
            suppliers={suppliers}
          />
        )}
      </div>
    </div>
  );
};

export default InvoiceDelivery;
